import { getSignedUrlForGet, useDirectorsByCorporation } from '@commenda/api';
import { Button, Icons, LoadingIndicator, useToggle } from '@commenda/kakashi';
import { ConfirmationModal } from '../../../../molecules/modals/ConfirmationModal';
import { downloadJsonFile } from '@commenda/utils/downloadFile';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus } from '../../../utils';
import { IndLLPIncorporationTasks } from '../../IN_LLP/ind-llp';

const unique = <T,>(arr: T[]): T[] =>
  arr.reduce((acc, el) => (acc.includes(el) ? acc : [...acc, el]), [] as T[]);

export const FilingSubmittedToMCATask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const { data: directors } = useDirectorsByCorporation(
    serviceRequest?.corporation?.id,
    undefined
  );

  const { setNotification } = useNotification();

  const [isDownloadingFillipFile, setIsDownloadingFillipFile] = useState(false);

  const filingSubmittedModalVisibility = useToggle();

  const handleMarkAsFiled = async () => {
    await updateTaskStatus({
      task,
      status: TaskStatus.COMPLETED,
      mutate,
    });
    filingSubmittedModalVisibility.hide();
  };

  async function downloadFillipFile() {
    if (isDownloadingFillipFile) {
      return;
    }

    const fillCorporationTask = serviceRequest.taskGroups
      .flatMap((taskGroup) => taskGroup.tasks)
      .find(
        (t) =>
          t.legacyServiceRequestTaskTaskId ===
          IndLLPIncorporationTasks.FILL_CORPORATION_DETAILS
      );

    // Workflow-driven requests hold the formation details in the questionnaire form, not a work request.
    if (!fillCorporationTask?.workRequest) {
      setNotification({
        show: true,
        valid: false,
        title: 'Export not available',
        message: 'Fill the MCA FiLLiP form directly.',
      });
      return;
    }

    function getFileIds(maybeObject: any): Array<number> {
      if (
        typeof maybeObject === 'object' &&
        maybeObject !== null &&
        'fileUrl' in maybeObject
      ) {
        return [maybeObject.id];
      }

      if (typeof maybeObject !== 'object' || maybeObject === null) {
        return [];
      }

      if (Array.isArray(maybeObject)) {
        return unique(maybeObject.flatMap(getFileIds));
      }

      return unique(Object.values(maybeObject).flatMap(getFileIds));
    }

    const fileToSignedUrl = (
      await Promise.all(
        getFileIds(serviceRequest).map(async (id) => {
          const res = await getSignedUrlForGet(id);
          return {
            id,
            signedUrl: res.signedUrl,
          };
        })
      )
    ).reduce(
      (acc, file) => ({
        ...acc,
        [file.id]: file.signedUrl,
      }),
      {} as Record<number, string>
    );

    const fillipJson = {
      version: 1,
      formCode: 'fillip',
      values: {
        workRequest: fillCorporationTask.workRequest,
        corporation: serviceRequest.corporation,
        directors,
        // form: {},
        TODO: {},
        fileToSignedUrl,
      },
      $schema: 'http://json-schema.org/draft-07/schema#',
    };

    downloadJsonFile(
      `${serviceRequest.company.companyDisplayName}.fillip.json`,
      fillipJson
    );
  }

  async function onDownloadFillipFileClick() {
    try {
      setIsDownloadingFillipFile(true);
      await downloadFillipFile();
    } catch (e) {
      setNotification({
        show: true,
        title: 'Error',
        message: 'Error downloading .fillip file',
        valid: false,
      });
    } finally {
      setIsDownloadingFillipFile(false);
    }
  }

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Agent submits filing to MCA" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Filing submitted to MCA by agent"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onDownloadFillipFileClick}
                  disabled={isDownloadingFillipFile}
                >
                  {isDownloadingFillipFile ? (
                    <LoadingIndicator size={16} />
                  ) : (
                    <Icons.Download01 className="size-4" />
                  )}{' '}
                  {isDownloadingFillipFile ? 'Downloading' : 'Download'}{' '}
                  .fillip.json file
                </Button>
                <Button
                  size="sm"
                  onClick={filingSubmittedModalVisibility.show}
                  variant="primary"
                >
                  <Icons.Check className="size-4" />
                  Mark as Filled
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel label="Mark update: Filing submitted to MCA" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                <Icons.Check className="size-4" />
                Mark as Filled
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Mark update: Filing submitted to MCA" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label=" Filing submitted to MCA"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        // Admin viewers run these steps too, and an unmapped role renders no actions.
        stateMap[state.role === TaskParty.ADMIN ? TaskParty.AGENT : state.role]?.[
          state.status
        ] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{
          role,
          status: task.status,
        }}
        context={context}
        config={config}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <ConfirmationModal
        isOpen={filingSubmittedModalVisibility.visible}
        onClose={filingSubmittedModalVisibility.hide}
        onConfirm={handleMarkAsFiled}
        title="Mark as filed"
        message="Please note that this action will update the customer. To revert this change, please contact Commenda Support."
        hideCancelButton
      />
    </div>
  );
};
